import { createSlice, createSelector } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { of } from "rxjs";
import { switchMap, map, catchError, withLatestFrom, exhaustMap } from "rxjs/operators";
import { CustomerGenders, RegistrationFormConfirmationTypes } from "@nxplatform/platform-entities";
import { RegistrationStepForm } from "@nxplatform/platform-entities/customer/customer-enum-types";
import { AppStorage } from "@service/api-client/http/helpers/AppStorage";
import { __ } from "@service/api-client/http/helpers/translates";
import { getCookie } from "@service/api-client/utils/get-cookie";
const IP_DATA_FEATURE_KEY = "ipData";
const IP_DATA_COOKIE_KEY = "activeCustomerCookie";
const nonAuthBonus = "nonAuthBonus";
var TaxIDValidationTypes = /* @__PURE__ */ ((TaxIDValidationTypes2) => {
  TaxIDValidationTypes2["passed"] = "passed";
  return TaxIDValidationTypes2;
})(TaxIDValidationTypes || {});
const ipDataCustomerKey = "ipDataSlice";
const initialIpDataState = {
  entities: AppStorage.get(ipDataCustomerKey) || null,
  loaded: !!AppStorage.get(ipDataCustomerKey),
  pending: false,
  error: null,
  taxIDSuccess: null,
  taxIDValidationLoading: false,
  customerTaxInfo: {
    name: null,
    surname: null,
    dob: null
  },
  registrationStarted: false,
  customer: {
    bonusType: null,
    gender: CustomerGenders.male,
    userRestrictions: {
      promo: {
        isBlockedVip: false,
        isBlockedPromotions: false
      }
    }
  },
  notification: {
    id: null,
    type: null,
    message: null,
    additionalData: {}
  },
  registrationSuccess: false,
  registrationCompleted: false,
  ibanVerification: false,
  registrationError: false,
  errorField: null,
  errorFields: /* @__PURE__ */ new Set(),
  emailError: null,
  registrationStep: 1,
  invalidRegistrationFieldsLoading: true,
  invalidRegistrationFieldsErrorMessages: {},
  invalidRegistrationFailedParams: {
    phone_format_start_zero: false
  },
  remainingSmsCodeAttempts: null,
  completeRegistrationLoading: false
};
const customerRegistrationType = "customer-register-data";
const ipDataSlice = createSlice({
  name: IP_DATA_FEATURE_KEY,
  initialState: initialIpDataState,
  reducers: {
    getIpDataStart: (state, action) => {
    },
    registrationStart: (state, action) => {
      state.registrationStarted = true;
    },
    registrationSuccess: (state, action) => {
      var _a, _b, _c, _d;
      if ((_b = (_a = action.payload) == null ? void 0 : _a.notification) == null ? void 0 : _b.ibanVerification) {
        state.ibanVerification = action.payload.notification.ibanVerification.required;
      }
      if ((_d = (_c = action.payload) == null ? void 0 : _c.notification) == null ? void 0 : _d.notification) {
        state.notification = action.payload.notification.notification;
      }
      state.registrationSuccess = true;
      state.errorField = null;
      state.errorFields = initialIpDataState.errorFields;
    },
    registrationFail: (state, action) => {
      state.registrationError = true;
      state.registrationStarted = false;
      state.taxIDSuccess = initialIpDataState.taxIDSuccess;
      if ("save" in state.customer) {
        state.customer.save = false;
      }
      if ((action == null ? void 0 : action.payload) && "response" in action.payload && action.payload.response && "data" in action.payload.response && typeof action.payload.response.data === "object" && Object.keys(action.payload.response.data)) {
        const { data } = action.payload.response;
        const fields = Object.keys(data);
        if (fields.includes(RegistrationStepForm.prefix) && fields.length > 1) {
          delete data[RegistrationStepForm.prefix];
        }
        const [firstField] = Object.keys(data);
        state.errorField = firstField;
        state.errorFields = new Set(Object.keys(data));
        state.error = data[firstField];
      }
    },
    emailCheckStart: (state, action) => {
      state.emailError = null;
    },
    emailCheckSuccess: (state, action) => {
      state.emailError = false;
    },
    emailCheckFail: (state, action) => {
      const [firstMessage] = action.payload.messages;
      state.emailError = firstMessage;
    },
    taxIDCheckStart: (state) => {
      state.taxIDSuccess = initialIpDataState.taxIDSuccess;
      state.taxIDValidationLoading = true;
    },
    taxIDCheckSuccess: (state, action) => {
      const { name, surname, dob, validation } = action.payload || {};
      if (validation === "passed") {
        state.taxIDSuccess = true;
        state.customerTaxInfo = {
          name,
          surname,
          dob
        };
      }
      state.taxIDValidationLoading = false;
    },
    taxIDCheckFail: (state, action) => {
      state.taxIDValidationLoading = false;
    },
    taxIDCheckClear: (state) => {
      state.taxIDSuccess = initialIpDataState.taxIDSuccess;
    },
    sendSmsStart: (state, action) => {
      state.error = null;
      state.pending = true;
    },
    sendSmsFail: (state, action) => {
      var _a;
      state.registrationError = true;
      if ("save" in state.customer) {
        state.customer.save = false;
      }
      const { messages = [], response = {} } = action.payload;
      const { data = {}, message = {} } = response;
      const { phone: phoneError } = data;
      const error = phoneError || messages[0];
      state.error = error;
      state.pending = false;
      state.remainingSmsCodeAttempts = (_a = message.params) == null ? void 0 : _a.remaining_attempts;
    },
    sendSmsSuccess: (state, action) => {
      const { remainingAttemptsRequests } = action.payload || {};
      state.error = null;
      state.pending = false;
      state.remainingSmsCodeAttempts = remainingAttemptsRequests;
    },
    clearCustomerStore: (state, action) => {
      const customerData = JSON.parse(JSON.stringify(state.customer));
      if (state.registrationSuccess) {
        state.customer = { ...initialIpDataState.customer };
        state.registrationStarted = false;
      }
      if (!state.registrationSuccess) {
        state.customer = {
          ...initialIpDataState.customer,
          promoCode: customerData.promoCode,
          bonusType: state.customer.bonusType,
          country: state.customer.country,
          currency: state.customer.currency,
          prefix: state.customer.prefix
        };
      }
      state.registrationStep = initialIpDataState.registrationStep;
      state.customer.recaptcha = customerData.recaptcha;
      state.error = null;
      state.pending = false;
      state.registrationSuccess = false;
    },
    getIpDataSuccess: (state, action) => {
      var _a;
      state.loaded = true;
      state.entities = { ...action.payload };
      state.country = (_a = action.payload) == null ? void 0 : _a.country;
    },
    getIpDataFailure: (state, action) => {
      state.error = action.payload;
    },
    changeCustomerData: (state, action) => {
      state.customer = {
        ...JSON.parse(JSON.stringify(state.customer)),
        ...JSON.parse(JSON.stringify(action.payload))
      };
    },
    deleteCustomerField: (state, action) => {
      if (action.payload in state.customer) {
        delete state.customer[action.payload];
      }
    },
    changeWelcomeBonusSuccess: (state, action) => {
    },
    changeWelcomeBonusFail: () => {
    },
    changeWelcomeBonusStart: () => {
    },
    clearErrorField: (state) => {
      state.errorField = null;
    },
    clearErrorsField: (state, action) => {
      state.errorFields.delete(action.payload);
    },
    clearIbanConfirmation: (state) => {
      state.ibanVerification = false;
    },
    registrationSuccessed: (state, action) => {
      state.registrationCompleted = action.payload;
    },
    changeRegistrationStep: (state, action) => {
      console.log("registration step changed", action.payload);
      state.registrationStep = action.payload;
    },
    setCountryFromCookie: (state, action) => {
      if (!action.payload) return;
      const { country } = action.payload;
      console.log("country from cookie", country);
      state.country = country;
      state.customer = { ...state.customer, country };
      state.entities = { ...state.entities, country, country_code: country };
    },
    getShuftiProVerifyLinkStart: (state) => {
    },
    getShuftiProVerifyLinkSuccess: (state, action) => {
    },
    getShuftiProVerifyLinkFail: (state, action) => {
    },
    verifyShuftiProTokenStart: (state, action) => {
    },
    verifyShuftiProTokenSuccess: (state, action) => {
    },
    verifyShuftiProTokenFail: (state, action) => {
    },
    registrationValidationStart: (state) => {
      state.invalidRegistrationFieldsLoading = true;
    },
    registrationValidationSuccess: (state, action) => {
      state.invalidRegistrationFieldsLoading = false;
      const successKeys = Object.keys(action.payload);
      successKeys.forEach((removeKey) => {
        delete state.invalidRegistrationFieldsErrorMessages[removeKey];
      });
      state.invalidRegistrationFailedParams = {
        phone_format_start_zero: false
      };
    },
    registrationValidationFail: (state, action) => {
      var _a, _b;
      state.invalidRegistrationFieldsLoading = false;
      state.invalidRegistrationFailedParams = (_a = action.payload) == null ? void 0 : _a.params;
      state.invalidRegistrationFieldsErrorMessages = Object.entries(((_b = action.payload) == null ? void 0 : _b.data) || {}).reduce((acc, [key, value]) => {
        var _a2;
        acc[key] = __(value, ((_a2 = action.payload) == null ? void 0 : _a2.params) || {});
        return acc;
      }, {});
    },
    clearRegistrationInvalidErrorMessages: (state) => {
      state.invalidRegistrationFieldsErrorMessages = {};
    },
    completeRegistrationStart: (state) => {
      state.completeRegistrationLoading = true;
    },
    completeRegistrationSuccess: () => {
    },
    completeRegistrationFail: (state, action) => {
      state.completeRegistrationLoading = false;
    },
    getUserRestrictionsStart: () => {
    },
    getUserRestrictionsSuccess: (state, action) => {
      state.customer.userRestrictions = {
        ...action.payload
      };
    },
    getUserRestrictionsFail: () => {
    }
  }
});
const ipDataReducer = ipDataSlice.reducer;
const {
  getIpDataStart,
  getIpDataSuccess,
  getIpDataFailure,
  registrationStart,
  changeCustomerData,
  registrationFail,
  registrationSuccess,
  emailCheckStart,
  emailCheckSuccess,
  emailCheckFail,
  taxIDCheckStart,
  taxIDCheckSuccess,
  taxIDCheckFail,
  taxIDCheckClear,
  clearCustomerStore,
  sendSmsStart,
  sendSmsSuccess,
  sendSmsFail,
  changeWelcomeBonusSuccess,
  changeWelcomeBonusFail,
  changeWelcomeBonusStart,
  clearErrorField,
  clearErrorsField,
  clearIbanConfirmation,
  registrationSuccessed,
  changeRegistrationStep,
  deleteCustomerField,
  setCountryFromCookie,
  getShuftiProVerifyLinkStart,
  getShuftiProVerifyLinkSuccess,
  getShuftiProVerifyLinkFail,
  verifyShuftiProTokenStart,
  verifyShuftiProTokenSuccess,
  verifyShuftiProTokenFail,
  registrationValidationStart,
  registrationValidationSuccess,
  registrationValidationFail,
  clearRegistrationInvalidErrorMessages,
  completeRegistrationStart,
  completeRegistrationSuccess,
  completeRegistrationFail,
  getUserRestrictionsStart,
  getUserRestrictionsSuccess,
  getUserRestrictionsFail
} = ipDataSlice.actions;
const getIpDataState = (rootState) => rootState[IP_DATA_FEATURE_KEY];
const selectIpDataEntities = createSelector(
  getIpDataState,
  (s) => s.entities
);
const selectCustomerData = createSelector(
  getIpDataState,
  (s) => s.customer
);
const selectCustomerPassword = createSelector(
  getIpDataState,
  (s) => s.customer.password
);
const selectCustomerBonusType = createSelector(
  getIpDataState,
  (s) => s.customer.bonusType
);
const selectIpDataLoaded = createSelector(
  getIpDataState,
  (s) => s.loaded
);
const selectCustomerIpCountry = createSelector(
  getIpDataState,
  (s) => s.country
);
const selectRegistrationSuccess = createSelector(
  getIpDataState,
  (s) => s.registrationSuccess
);
const selectRegistrationCompleted = createSelector(
  getIpDataState,
  (s) => s.registrationCompleted
);
const selectIbanVerificationNeeded = createSelector(
  getIpDataState,
  (s) => s.ibanVerification
);
const selectRegistrationSuccessNotification = createSelector(
  getIpDataState,
  (s) => s.notification
);
const selectEmailCheckFail = createSelector(
  getIpDataState,
  (s) => s.emailError
);
const selectRegistrationStarted = createSelector(
  getIpDataState,
  (s) => s.registrationStarted
);
const isTaxIDValidationSuccessful = createSelector(
  getIpDataState,
  ({ taxIDSuccess }) => taxIDSuccess
);
const isTaxIDValidationLoading = createSelector(
  getIpDataState,
  ({ taxIDValidationLoading }) => taxIDValidationLoading
);
const selectTaxCustomerInfo = createSelector(
  getIpDataState,
  (s) => s.customerTaxInfo
);
const selectRegistrationFieldWithError = createSelector(
  getIpDataState,
  (s) => s.errorField
);
const selectConfirmationProcess = createSelector(
  getIpDataState,
  (s) => s.pending
);
const selectIpDataError = createSelector(getIpDataState, (s) => s.error);
const selectCustomerGender = createSelector(
  getIpDataState,
  (s) => s.customer.gender
);
const selectRegistrationStep = createSelector(
  getIpDataState,
  (s) => s.registrationStep
);
const isRequiredCountry = createSelector(
  getIpDataState,
  (s, countries) => (countries || []).filter(
    (country) => !!country.requiredRegistrationSmsVerification
  ),
  (s, countries) => !!countries.find(
    (country) => {
      var _a;
      return country.id.toLowerCase() === ((_a = s.customer.country) == null ? void 0 : _a.toLocaleLowerCase());
    }
  )
);
const selectCustomerShuftiProToken = createSelector(
  getIpDataState,
  (s) => s.customer.shuftiProToken
);
const selectRegistrationValidationLoaded = createSelector(
  getIpDataState,
  (s) => s.invalidRegistrationFieldsLoading
);
const selectRegistrationValidationPhoneError = createSelector(
  getIpDataState,
  (s) => s.invalidRegistrationFailedParams.phone_format_start_zero
);
const selectInvalidRegistrationFieldsErrorMessages = createSelector(
  getIpDataState,
  (s) => s.invalidRegistrationFieldsErrorMessages
);
const selectIpDataTimeZone = createSelector(
  getIpDataState,
  (s) => {
    var _a;
    return (_a = s.entities) == null ? void 0 : _a.timezone;
  }
);
const selectIpDataCurrency = createSelector(
  getIpDataState,
  (s) => {
    var _a;
    return (_a = s.entities) == null ? void 0 : _a.currency;
  }
);
const selectCustomerCountry = createSelector(
  getIpDataState,
  (s) => s.customer.country
);
const selectIpDataCountryCodeIso3 = createSelector(
  getIpDataState,
  (s) => s.entities && s.entities.country_code_iso3
);
const selectSmsConfirmationAttempts = createSelector(
  getIpDataState,
  ({ remainingSmsCodeAttempts }) => remainingSmsCodeAttempts
);
const selectIsCompleteRegistrationLoading = createSelector(
  getIpDataState,
  ({ completeRegistrationLoading }) => completeRegistrationLoading
);
const checkCustomerErrorField = createSelector(
  getIpDataState,
  (s, fieldName) => fieldName,
  ({ errorFields }, fieldName) => errorFields.has(fieldName)
);
const ipDataEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getIpDataStart.type),
  switchMap(() => Api.geoClient.getCustomerIpData().pipe(
    map((customer) => getIpDataSuccess(customer)),
    catchError((err) => of(getIpDataFailure(err)))
  ))
);
const cookieCountryEpic = (action$) => action$.pipe(
  ofType(getIpDataStart.type, changeCustomerData.type),
  map(() => {
    const cookieApiData = getCookie(IP_DATA_COOKIE_KEY);
    const correctCookieApiData = cookieApiData ? JSON.parse(cookieApiData) : null;
    return setCountryFromCookie(correctCookieApiData);
  })
);
const registerCustomer = (customer, headers) => ({
  type: registrationStart.type,
  payload: {
    customer,
    headers
  }
});
const registrationEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(registrationStart.type),
  withLatestFrom(state$),
  exhaustMap(([action, state]) => {
    var _a;
    const useV3Api = !!((_a = state.registrationSettings) == null ? void 0 : _a.configurableRegistration);
    const { customer, headers } = action.payload || {};
    const customerRegister = Api.authentication.customerRegister(
      customer,
      useV3Api,
      headers
    );
    return customerRegister.pipe(
      map((data) => registrationSuccess(data)),
      catchError((err) => of(registrationFail(err)))
    );
  })
);
const checkIfEmailExistsEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(emailCheckStart().type),
  switchMap((action) => {
    const customerCheckEmail = Api.authentication.customerCheckEmail(
      action.payload
    );
    return customerCheckEmail.pipe(
      map((data) => emailCheckSuccess()),
      catchError((err) => of(emailCheckFail(err)))
    );
  })
);
const checkTaxIDValidationEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(taxIDCheckStart.type),
  switchMap((action) => {
    const customerCheckEmail = Api.customer.taxIdValidation(
      action.payload
    );
    return customerCheckEmail.pipe(
      map((data) => taxIDCheckSuccess(data)),
      catchError((err) => of(taxIDCheckFail(err)))
    );
  })
);
const checkTaxIDValidation = (params) => ({
  type: taxIDCheckStart.type,
  payload: params
});
const prepareConfirmationRequestedData = ({
  countryCode,
  type,
  email,
  locale,
  phone,
  prefix,
  recaptcha
}) => {
  const baseData = {
    type,
    locale,
    country: countryCode,
    recaptcha
  };
  if (type === RegistrationFormConfirmationTypes.phone) {
    const phoneWithPrefix = `${prefix}${phone}`;
    return {
      ...baseData,
      phone: phoneWithPrefix
    };
  }
  return {
    ...baseData,
    email
  };
};
const sendSmsEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(sendSmsStart().type),
  switchMap((action) => {
    const {
      countryCode,
      type,
      email,
      locale,
      prefix,
      phone,
      recaptcha
    } = action.payload;
    const requestedData = prepareConfirmationRequestedData({
      countryCode,
      type,
      email,
      locale,
      phone,
      prefix,
      recaptcha
    });
    const sendSms = Api.authentication.sendSmsCode(requestedData);
    return sendSms.pipe(
      map((data) => sendSmsSuccess(data)),
      catchError((err) => of(sendSmsFail(err)))
    );
  })
);
const updateCustomerBonusTypeEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(changeWelcomeBonusStart.type),
  switchMap(
    (action) => Api.customer.updateCustomerBonusType(
      action.payload
    ).pipe(
      map(() => changeWelcomeBonusSuccess(action.payload)),
      catchError(() => of(changeWelcomeBonusFail()))
    )
  )
);
const customerUserRestrictionsEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getUserRestrictionsStart.type),
  switchMap(() => Api.customer.getUserRestrictions().pipe(
    map((data) => getUserRestrictionsSuccess(data)),
    catchError(() => of(getUserRestrictionsFail()))
  ))
);
const fetchCustomerUserRestrictions = () => ({
  type: getUserRestrictionsStart.type
});
const updateCustomerBonusType = (customer, bonusType) => ({ type: changeWelcomeBonusStart.type, payload: { customer, bonusType } });
const fetchCustomerIpData = () => ({ type: getIpDataStart.type });
const checkIfEmailExists = (email) => ({
  type: emailCheckStart().type,
  payload: email
});
const shuftiProVerifyLinkRequest = (payload) => ({
  type: getShuftiProVerifyLinkStart.type,
  payload
});
const shuftiProVerifyLinkRequestEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getShuftiProVerifyLinkStart.type),
  switchMap((action) => Api.customer.getShuftiProVerifyLink(action.payload).pipe(
    map((data) => getShuftiProVerifyLinkSuccess(data)),
    catchError((err) => of(getShuftiProVerifyLinkFail(err)))
  ))
);
const shuftiProVerifyToken = (payload) => ({
  type: verifyShuftiProTokenStart.type,
  payload
});
const shuftiProVerifyTokenEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(verifyShuftiProTokenStart.type),
  switchMap((action) => Api.customer.getShufriProVerificationStatus(action.payload).pipe(
    map(() => verifyShuftiProTokenSuccess(action.payload)),
    catchError((err) => of(verifyShuftiProTokenFail(err)))
  ))
);
const registrationValidationEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(registrationValidationStart.type),
  switchMap(({
    payload: { allFieldsEndpoint, ...rest }
  }) => Api.authentication.getRegistrationValidationStatus(rest, allFieldsEndpoint).pipe(
    map(() => registrationValidationSuccess(rest)),
    catchError((error) => of(registrationValidationFail(error.response)))
  ))
);
const registrationValidation = (payload, allFieldsEndpoint) => ({
  type: registrationValidationStart.type,
  payload: { ...payload, allFieldsEndpoint }
});
const completeRegistrationEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(completeRegistrationStart.type),
  switchMap((action) => Api.customer.completeRegistration(action.payload).pipe(
    map(() => completeRegistrationSuccess()),
    catchError((err) => of(completeRegistrationFail(err)))
  ))
);
const completeRegistration = (payload) => ({
  type: completeRegistrationStart.type,
  payload
});
const changeCustomerDataAction = (payload) => ({
  type: changeCustomerData.type,
  payload
});
export {
  IP_DATA_COOKIE_KEY,
  IP_DATA_FEATURE_KEY,
  TaxIDValidationTypes,
  changeCustomerData,
  changeCustomerDataAction,
  changeRegistrationStep,
  changeWelcomeBonusFail,
  changeWelcomeBonusStart,
  changeWelcomeBonusSuccess,
  checkCustomerErrorField,
  checkIfEmailExists,
  checkIfEmailExistsEpic,
  checkTaxIDValidation,
  checkTaxIDValidationEpic,
  clearCustomerStore,
  clearErrorField,
  clearErrorsField,
  clearIbanConfirmation,
  clearRegistrationInvalidErrorMessages,
  completeRegistration,
  completeRegistrationEpic,
  completeRegistrationFail,
  completeRegistrationStart,
  completeRegistrationSuccess,
  cookieCountryEpic,
  customerRegistrationType,
  customerUserRestrictionsEpic,
  deleteCustomerField,
  emailCheckFail,
  emailCheckStart,
  emailCheckSuccess,
  fetchCustomerIpData,
  fetchCustomerUserRestrictions,
  getIpDataFailure,
  getIpDataStart,
  getIpDataState,
  getIpDataSuccess,
  getShuftiProVerifyLinkFail,
  getShuftiProVerifyLinkStart,
  getShuftiProVerifyLinkSuccess,
  getUserRestrictionsFail,
  getUserRestrictionsStart,
  getUserRestrictionsSuccess,
  initialIpDataState,
  ipDataCustomerKey,
  ipDataEpic,
  ipDataReducer,
  ipDataSlice,
  isRequiredCountry,
  isTaxIDValidationLoading,
  isTaxIDValidationSuccessful,
  nonAuthBonus,
  registerCustomer,
  registrationEpic,
  registrationFail,
  registrationStart,
  registrationSuccess,
  registrationSuccessed,
  registrationValidation,
  registrationValidationEpic,
  registrationValidationFail,
  registrationValidationStart,
  registrationValidationSuccess,
  selectConfirmationProcess,
  selectCustomerBonusType,
  selectCustomerCountry,
  selectCustomerData,
  selectCustomerGender,
  selectCustomerIpCountry,
  selectCustomerPassword,
  selectCustomerShuftiProToken,
  selectEmailCheckFail,
  selectIbanVerificationNeeded,
  selectInvalidRegistrationFieldsErrorMessages,
  selectIpDataCountryCodeIso3,
  selectIpDataCurrency,
  selectIpDataEntities,
  selectIpDataError,
  selectIpDataLoaded,
  selectIpDataTimeZone,
  selectIsCompleteRegistrationLoading,
  selectRegistrationCompleted,
  selectRegistrationFieldWithError,
  selectRegistrationStarted,
  selectRegistrationStep,
  selectRegistrationSuccess,
  selectRegistrationSuccessNotification,
  selectRegistrationValidationLoaded,
  selectRegistrationValidationPhoneError,
  selectSmsConfirmationAttempts,
  selectTaxCustomerInfo,
  sendSmsEpic,
  sendSmsFail,
  sendSmsStart,
  sendSmsSuccess,
  setCountryFromCookie,
  shuftiProVerifyLinkRequest,
  shuftiProVerifyLinkRequestEpic,
  shuftiProVerifyToken,
  shuftiProVerifyTokenEpic,
  taxIDCheckClear,
  taxIDCheckFail,
  taxIDCheckStart,
  taxIDCheckSuccess,
  updateCustomerBonusType,
  updateCustomerBonusTypeEpic,
  verifyShuftiProTokenFail,
  verifyShuftiProTokenStart,
  verifyShuftiProTokenSuccess
};
